import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Clock, ChevronUp, ChevronDown, Loader2, Settings2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = () => ({
  name: '', kuerzel: '', startzeit: '06:00', endzeit: '14:00', order: 0,
});

export const SchichtConfig = () => {
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [schichten, setSchichten] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    if (selectedLocationId) fetchSchichten(selectedLocationId);
    else setSchichten([]);
  }, [selectedLocationId]);

  const fetchLocations = async () => {
    try {
      const r = await axios.get(`${API}/user/locations`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const locs = r.data || [];
      setLocations(locs);
      if (locs.length > 0) setSelectedLocationId(locs[0].id);
    } catch { toast.error('Standorte konnten nicht geladen werden'); }
  };

  const fetchSchichten = async (locId) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/tasks/schichten?location_id=${locId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setSchichten(r.data || []);
    } catch { toast.error('Schichten konnten nicht geladen werden'); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditId(null);
    setForm({ ...emptyForm(), order: schichten.length });
    setModalOpen(true);
  };

  const openEdit = (s) => {
    setEditId(s.id);
    setForm({ name: s.name, kuerzel: s.kuerzel, startzeit: s.startzeit, endzeit: s.endzeit, order: s.order });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.kuerzel.trim()) {
      toast.error('Name und Kürzel sind Pflichtfelder');
      return;
    }
    if (form.kuerzel.length > 5) {
      toast.error('Kürzel darf maximal 5 Zeichen haben');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      if (editId) {
        await axios.put(`${API}/tasks/schichten/${editId}`, form, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Schicht aktualisiert');
      } else {
        await axios.post(`${API}/tasks/schichten`, { ...form, location_id: selectedLocationId }, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Schicht erstellt');
      }
      setModalOpen(false);
      fetchSchichten(selectedLocationId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Schicht wirklich löschen?')) return;
    try {
      await axios.delete(`${API}/tasks/schichten/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      toast.success('Schicht gelöscht');
      fetchSchichten(selectedLocationId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleReorder = async (idx, dir) => {
    const arr = [...schichten];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= arr.length) return;
    [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
    // Update order values
    const updates = arr.map((s, i) => axios.put(`${API}/tasks/schichten/${s.id}`, { order: i }, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    }));
    try {
      await Promise.all(updates);
      setSchichten(arr.map((s, i) => ({ ...s, order: i })));
    } catch { toast.error('Reihenfolge konnte nicht gespeichert werden'); }
  };

  const SHIFT_COLORS = [
    'from-orange-500 to-amber-500',
    'from-blue-500 to-cyan-500',
    'from-violet-500 to-purple-500',
    'from-emerald-500 to-green-500',
    'from-rose-500 to-pink-500',
    'from-slate-500 to-gray-600',
  ];

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Settings2 className="h-6 w-6 text-primary" />
              Schicht-Konfiguration
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Definiere die Schichten für dein Team – standortspezifisch und flexibel.
            </p>
          </div>
          <Button
            onClick={openCreate}
            disabled={!selectedLocationId}
            data-testid="create-schicht-btn"
            className="flex items-center gap-2 shrink-0"
          >
            <Plus className="h-4 w-4" />
            Neue Schicht
          </Button>
        </div>

        {/* Standort-Auswahl */}
        {locations.length > 1 && (
          <div className="space-y-1">
            <Label className="text-sm font-medium">Standort</Label>
            <Select value={selectedLocationId} onValueChange={setSelectedLocationId}>
              <SelectTrigger className="w-full sm:w-64" data-testid="location-select">
                <SelectValue placeholder="Standort wählen" />
              </SelectTrigger>
              <SelectContent>
                {locations.map(l => (
                  <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Schichten-Liste */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : schichten.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center space-y-3">
              <Clock className="h-12 w-12 text-muted-foreground/40 mx-auto" />
              <p className="text-muted-foreground font-medium">Noch keine Schichten angelegt</p>
              <p className="text-sm text-muted-foreground/70">
                Erstelle die erste Schicht für diesen Standort.
              </p>
              <Button onClick={openCreate} variant="outline" className="mt-2">
                <Plus className="h-4 w-4 mr-2" /> Erste Schicht anlegen
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {schichten.map((s, idx) => (
              <Card key={s.id} className="shadow-sm hover:shadow-md transition-shadow" data-testid={`schicht-card-${s.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    {/* Kürzel Badge */}
                    <div className={`bg-gradient-to-br ${SHIFT_COLORS[idx % SHIFT_COLORS.length]} rounded-xl w-14 h-14 flex items-center justify-center shrink-0`}>
                      <span className="text-white font-bold text-sm leading-tight text-center px-1">{s.kuerzel}</span>
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-foreground truncate">{s.name}</h3>
                      <div className="flex items-center gap-1 mt-1 text-sm text-muted-foreground">
                        <Clock className="h-3.5 w-3.5 shrink-0" />
                        <span>{s.startzeit} – {s.endzeit} Uhr</span>
                      </div>
                    </div>

                    {/* Reorder + Actions */}
                    <div className="flex flex-col gap-1 shrink-0">
                      <button
                        onClick={() => handleReorder(idx, -1)}
                        disabled={idx === 0}
                        className="p-1 rounded hover:bg-muted disabled:opacity-30"
                        title="Nach oben"
                      >
                        <ChevronUp className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => handleReorder(idx, 1)}
                        disabled={idx === schichten.length - 1}
                        className="p-1 rounded hover:bg-muted disabled:opacity-30"
                        title="Nach unten"
                      >
                        <ChevronDown className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button
                        variant="ghost" size="icon"
                        onClick={() => openEdit(s)}
                        data-testid={`edit-schicht-${s.id}`}
                        className="h-8 w-8"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost" size="icon"
                        onClick={() => handleDelete(s.id)}
                        data-testid={`delete-schicht-${s.id}`}
                        className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Hinweis */}
        {schichten.length > 0 && (
          <p className="text-xs text-muted-foreground text-center">
            {schichten.length} Schicht{schichten.length !== 1 ? 'en' : ''} definiert · Reihenfolge mit ↑↓ anpassen
          </p>
        )}
      </div>

      {/* Modal: Schicht anlegen / bearbeiten */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-md" data-testid="schicht-modal">
          <DialogHeader>
            <DialogTitle>{editId ? 'Schicht bearbeiten' : 'Neue Schicht anlegen'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="schicht-name">Schicht-Name *</Label>
              <Input
                id="schicht-name"
                placeholder="z.B. Frühschicht"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                data-testid="schicht-name-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="schicht-kuerzel">
                Kürzel * <span className="text-muted-foreground font-normal">(max. 5 Zeichen, z.B. FS11)</span>
              </Label>
              <Input
                id="schicht-kuerzel"
                placeholder="z.B. FS11"
                maxLength={5}
                value={form.kuerzel}
                onChange={e => setForm(f => ({ ...f, kuerzel: e.target.value.toUpperCase() }))}
                data-testid="schicht-kuerzel-input"
                className="uppercase font-mono"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="schicht-start">Startzeit</Label>
                <Input
                  id="schicht-start"
                  type="time"
                  value={form.startzeit}
                  onChange={e => setForm(f => ({ ...f, startzeit: e.target.value }))}
                  data-testid="schicht-start-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="schicht-end">Endzeit</Label>
                <Input
                  id="schicht-end"
                  type="time"
                  value={form.endzeit}
                  onChange={e => setForm(f => ({ ...f, endzeit: e.target.value }))}
                  data-testid="schicht-end-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setModalOpen(false)}>Abbrechen</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-schicht-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editId ? 'Aktualisieren' : 'Erstellen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default SchichtConfig;
